import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";
import { useTrialModal } from "@/context/TrialModalContext";

const LINKS = [
  { label: "Meet DOX", id: "meet-dox" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Features", id: "features" },
  { label: "Pricing", id: "pricing" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openTrial } = useTrialModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`lg:fixed lg:top-0 lg:inset-x-0 relative z-50 transition-[background,padding] duration-300 ${
        scrolled
          ? "py-4 lg:py-5 bg-[rgba(6,10,25,0.85)] backdrop-blur-2xl"
          : "py-5 lg:py-8 bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-14 flex items-center justify-between gap-8">
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/")}
          className="relative flex items-center shrink-0 group"
          aria-label="EZDoxAI home"
        >
          <Logo maxWidth={620} />
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-dox-orange to-dox-purple transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <button
            data-testid="nav-login-btn"
            onClick={() => navigate(user ? "/dashboard" : "/login")}
            className="text-sm font-semibold text-white/80 hover:text-white px-4 py-2 transition-colors"
          >
            {user ? "Dashboard" : "Login"}
          </button>
          <button
            data-testid="nav-trial-btn"
            onClick={() => (user ? navigate("/dashboard") : openTrial())}
            className="rounded-full bg-dox-orange px-5 py-2.5 min-h-[44px] text-sm font-bold text-white shadow-[0_0_24px_rgba(30,120,255,0.45)] hover:bg-dox-orangeHover hover:scale-[1.02] active:scale-95 transition-all"
          >
            {user ? "Dashboard" : "Start Your Free Trial"}
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10 mt-3 px-6 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left text-base font-medium text-white/80"
            >
              {l.label}
            </button>
          ))}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="flex-1 rounded-full border border-white/20 py-2.5 text-sm font-semibold"
            >
              {user ? "Dashboard" : "Login"}
            </button>
            <button
              onClick={() => {
                setOpen(false);
                user ? navigate("/dashboard") : openTrial();
              }}
              className="flex-1 rounded-full bg-dox-orange py-2.5 min-h-[44px] text-sm font-bold"
            >
              {user ? "Dashboard" : "Start Your Free Trial"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
